import { z } from 'zod';
import {
  EXTRACTION_MAX_BYTES,
  extractionIdSchema,
  isPhotoExtractionId,
} from '../generation/studyMaterial';
import { domainSchema, MAX_SOURCE_SITES } from '../source/source';
import {
  ageGroupsSchema,
  languageSchema,
  targetLanguagesSchema,
  textSchema,
  timestampSchema,
} from '../task/task';
import {
  jobMethodSchema,
  jobRequestSchema,
  jobSchema,
  jobTaskCountSchema,
  microUsdSchema,
  writeModelSchema,
} from './job';

/** The generation jobs collection path (QZ-D48). */
export const JOBS_PATH = '/jobs';

/** A single job's path (QZ-D48). */
export const jobPath = (jobId: string): string => `/jobs/${encodeURIComponent(jobId)}`;

/** A single job's cancel path (QZ-D57). */
export const jobCancelPath = (jobId: string): string => `${jobPath(jobId)}/cancel`;

/** The router pattern matching `jobPath` (QZ-D48). */
export const JOB_PATH_PATTERN = '/jobs/:jobId';

/** The router pattern matching `jobCancelPath` (QZ-D57). */
export const JOB_CANCEL_PATH_PATTERN = '/jobs/:jobId/cancel';

/** The number of jobs returned by one page of `GET /jobs` (QZ-D55). */
export const JOB_PAGE_SIZE = 25;

/** The spend totals path (QZ-D55). */
export const SPEND_PATH = '/spend';

/**
 * The body accepted by `POST /jobs` (QZ-D48, PDG-D2). `method` is optional with no zod
 * `.default`: absent means `'prompt'`, which `newJob` applies. A `.default` would make `method`
 * required in `CreateJobRequest`, breaking every existing request literal and
 * `generateJob_test`'s `toEqual` on the parsed body.
 */
export const createJobRequestSchema = z
  .object({
    method: jobMethodSchema.optional(),
    request: jobRequestSchema.optional(),
    instructions: jobRequestSchema.optional(),
    materialName: textSchema.max(200).optional(),
    /** The section of the document a PDF or photo job names, if any (QZ-D100, QZ-D101). */
    section: textSchema.max(200).optional(),
    /** The extraction a PDF job's material is cut from (PTX-D4). */
    extractionId: extractionIdSchema.optional(),
    /** A PDF job's page range into the extraction; both default to the whole PDF (PTX-D5). */
    pageFrom: z.number().int().min(1).optional(),
    pageTo: z.number().int().min(1).optional(),
    language: languageSchema,
    taskCount: jobTaskCountSchema,
    /** Omitted means every language (QZ-D130); `newJob` then stores all of `LANGUAGES`. */
    targetLanguages: targetLanguagesSchema.optional(),
    /** Omitted means every age group (QZ-D131); `newJob` then stores all of `AGE_GROUPS`. */
    ageGroups: ageGroupsSchema.optional(),
    /** Omitted means `DEFAULT_WRITE_MODEL`. */
    writeModel: writeModelSchema.optional(),
    /**
     * A prompt job's pick from the source sites. Omitted means every site; empty means the open
     * web, where the tasks cite no source.
     */
    sourceSites: z.array(domainSchema).max(MAX_SOURCE_SITES).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.sourceSites && new Set(value.sourceSites).size !== value.sourceSites.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['sourceSites'],
        message: 'A source site may be picked only once.',
      });
    }
    if (
      value.targetLanguages &&
      new Set(value.targetLanguages).size !== value.targetLanguages.length
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['targetLanguages'],
        message: 'A language may be picked only once.',
      });
    }
    if (value.targetLanguages && !value.targetLanguages.includes(value.language)) {
      ctx.addIssue({
        code: 'custom',
        path: ['targetLanguages'],
        message: 'The target languages must include the job language.',
      });
    }
    if (value.ageGroups && new Set(value.ageGroups).size !== value.ageGroups.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['ageGroups'],
        message: 'An age group may be picked only once.',
      });
    }
    const method = value.method ?? 'prompt';
    if (method === 'prompt') {
      if (value.request === undefined) {
        ctx.addIssue({ code: 'custom', path: ['request'], message: 'A request is required.' });
      }
      if (value.materialName !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['materialName'],
          message: 'A prompt job must not have a material name.',
        });
      }
      if (value.section !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['section'],
          message: 'A prompt job takes no section.',
        });
      }
      if (value.instructions !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['instructions'],
          message: 'A prompt job must not have instructions.',
        });
      }
      if (value.extractionId !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['extractionId'],
          message: 'A prompt job must not have an extraction id.',
        });
      }
      if (value.pageFrom !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['pageFrom'],
          message: 'A prompt job must not have a page range.',
        });
      }
      if (value.pageTo !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['pageTo'],
          message: 'A prompt job must not have a page range.',
        });
      }
    } else {
      // Common to both material methods (pdf and photos).
      if (value.extractionId === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['extractionId'],
          message: 'An extraction id is required.',
        });
      }
      if (value.materialName === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['materialName'],
          message: 'A material name is required.',
        });
      }
      if (value.request !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['request'],
          message: 'A PDF or photo job must not have a request.',
        });
      }
      if (value.sourceSites !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['sourceSites'],
          message: 'A PDF or photo job must not have source sites.',
        });
      }

      if (method === 'photos') {
        // PHO-D5: a photo set has no page range; page N is the Nth photo in admin order.
        if (value.pageFrom !== undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['pageFrom'],
            message: 'A photo job has no page range.',
          });
        }
        if (value.pageTo !== undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['pageTo'],
            message: 'A photo job has no page range.',
          });
        }
        if (value.extractionId !== undefined && !isPhotoExtractionId(value.extractionId)) {
          ctx.addIssue({
            code: 'custom',
            path: ['extractionId'],
            message: "A photo job needs a photo set's extraction id.",
          });
        }
      } else {
        if (value.extractionId === undefined) {
          if (value.pageFrom !== undefined) {
            ctx.addIssue({
              code: 'custom',
              path: ['pageFrom'],
              message: 'A page range needs an extraction id.',
            });
          }
          if (value.pageTo !== undefined) {
            ctx.addIssue({
              code: 'custom',
              path: ['pageTo'],
              message: 'A page range needs an extraction id.',
            });
          }
        } else if (
          value.pageFrom !== undefined &&
          value.pageTo !== undefined &&
          value.pageFrom > value.pageTo
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['pageTo'],
            message: 'pageTo must not be before pageFrom.',
          });
        }
        if (value.extractionId !== undefined && isPhotoExtractionId(value.extractionId)) {
          ctx.addIssue({
            code: 'custom',
            path: ['extractionId'],
            message: "A PDF job must not use a photo set's extraction id.",
          });
        }
      }
    }
  });
export type CreateJobRequest = z.infer<typeof createJobRequestSchema>;

/** The extractions collection path (PTX-D4). */
export const EXTRACTIONS_PATH = '/jobs/extractions';

/** A single extraction's path (PTX-D4). */
export const extractionPath = (id: string): string =>
  `${EXTRACTIONS_PATH}/${encodeURIComponent(id)}`;

/** The router pattern matching `extractionPath` (PTX-D4). */
export const EXTRACTION_PATH_PATTERN = '/jobs/extractions/:id';

/** A single extraction page's path (PTX-D4). */
export const extractionPagePath = (id: string, page: number): string =>
  `${extractionPath(id)}/pages/${encodeURIComponent(page)}`;

/** The router pattern matching `extractionPagePath` (PTX-D4). */
export const EXTRACTION_PAGE_PATH_PATTERN = '/jobs/extractions/:id/pages/:page';

/** A single job material page's path (PTX-D4). */
export const jobMaterialPagePath = (jobId: string, page: number): string =>
  `${jobPath(jobId)}/material/pages/${encodeURIComponent(page)}`;

/** The router pattern matching `jobMaterialPagePath` (PTX-D4). */
export const JOB_MATERIAL_PAGE_PATH_PATTERN = '/jobs/:jobId/material/pages/:page';

/** The body returned by `GET /jobs/extractions/:id` (PTX-D4, PTX-D14). */
export const extractionSummarySchema = z
  .object({
    id: extractionIdSchema,
    pdfPageCount: z.number().int().min(1),
    pageChars: z.array(z.number().int().min(0)),
  })
  .strip();
export type ExtractionSummary = z.infer<typeof extractionSummarySchema>;

/** The body accepted by `POST /jobs/extractions` (PTX-D4). */
export const createExtractionUploadRequestSchema = z
  .object({
    id: extractionIdSchema,
    sizeBytes: z.number().int().min(1).max(EXTRACTION_MAX_BYTES),
  })
  .strict();
export type CreateExtractionUploadRequest = z.infer<typeof createExtractionUploadRequestSchema>;

/** The body returned by `POST /jobs/extractions` (PTX-D4). */
export const extractionUploadResponseSchema = z
  .object({
    uploadUrl: z.url(),
    expiresAt: timestampSchema,
  })
  .strip();
export type ExtractionUploadResponse = z.infer<typeof extractionUploadResponseSchema>;

/**
 * The body returned by `GET /jobs/extractions/:id/pages/:page` and
 * `GET /jobs/:jobId/material/pages/:page` (PTX-D4).
 */
export const materialPageResponseSchema = z
  .object({
    page: z.number().int().min(1),
    text: z.string(),
  })
  .strip();
export type MaterialPageResponse = z.infer<typeof materialPageResponseSchema>;

/** The query string accepted by `GET /jobs` (QZ-D55). */
export const listJobsQuerySchema = z.object({ cursor: z.string().min(1).optional() }).strict();
export type ListJobsQuery = z.infer<typeof listJobsQuerySchema>;

/** The body returned by `GET /jobs` (QZ-D55). */
export const listJobsResponseSchema = z
  .object({
    jobs: z.array(jobSchema),
    nextCursor: z.string().min(1).optional(),
  })
  .strip();
export type ListJobsResponse = z.infer<typeof listJobsResponseSchema>;

/** The body returned by `GET /spend` (QZ-D55, QZ-D117). */
export const spendResponseSchema = z
  .object({
    allTimeMicroUsd: microUsdSchema,
    thisMonthMicroUsd: microUsdSchema,
    translationAllTimeMicroUsd: microUsdSchema,
    translationThisMonthMicroUsd: microUsdSchema,
    month: z.string().regex(/^\d{4}-\d{2}$/),
  })
  .strip();
export type SpendResponse = z.infer<typeof spendResponseSchema>;
